import React, { useState, useEffect } from 'react';
import { baseUrl } from '../../shared';
import dayjs from 'dayjs';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';

import TorqueWrenchCalForm from './TorqueWrenchCalForm';
import HydPressureCalForm from './HydPressureCalForm';
import PneumaticCalForm from './PneumaticCalForm';
import GaugeCalForm from './GaugeCalForm';
import HoseTestCalForm from './HoseTestCalForm';

const authHeaders = () => ({
  username: localStorage.getItem('username'),
  bToken: localStorage.getItem('bToken'),
});

export const CAL_TYPE_LABELS = {
  HYD_TW: 'Hydraulic Torque Wrench',
  BATT_TW: 'Battery Torque Wrench',
  HYD_PRESSURE: 'Hydraulic Pressure/Torque',
  PNU: 'Pneumatic',
  GAUGE: 'Gauge Calibration',
  DIAL: 'Dial Indicator',
  HOSE_MDG41: 'Hydraulic Hose (MDG41)',
  HOSE_JACK: 'Hose/Jack Test',
};

const CAL_TYPE_GROUPS = [
  { label: 'Torque Wrench',       types: ['HYD_TW', 'BATT_TW'] },
  { label: 'Hydraulic Pressure',  types: ['HYD_PRESSURE'] },
  { label: 'Pneumatic',           types: ['PNU'] },
  { label: 'Gauge / Dial',        types: ['GAUGE', 'DIAL'] },
  { label: 'Hose / Jack',         types: ['HOSE_MDG41', 'HOSE_JACK'] },
];

function TypePickerDialog({ open, onClose, onSelect, allowedCalTypes }) {
  const visibleGroups = CAL_TYPE_GROUPS
    .map(group => ({
      ...group,
      types: allowedCalTypes ? group.types.filter(t => allowedCalTypes.includes(t)) : group.types,
    }))
    .filter(group => group.types.length > 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Select Calibration Type</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <List disablePadding>
          {visibleGroups.map(group => (
            <React.Fragment key={group.label}>
              <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', fontSize: 10 }}>
                  {group.label}
                </Typography>
              </Box>
              {group.types.map(type => (
                <ListItemButton key={type} onClick={() => onSelect(type)} sx={{ py: 0.75, pl: 3 }}>
                  <ListItemText primary={CAL_TYPE_LABELS[type]} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItemButton>
              ))}
            </React.Fragment>
          ))}
          {visibleGroups.length === 0 && (
            <Box sx={{ px: 2, py: 2 }}>
              <Typography variant="body2" color="text.secondary">No calibration types configured for this asset type.</Typography>
            </Box>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size="small">Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}

function CalForm({ type, open, onClose, job, cal, canManage, onSaved }) {
  if (!open) return null;
  const props = { open, onClose, job, cal, canManage, onSaved };
  if (type === 'HYD_TW' || type === 'BATT_TW') return <TorqueWrenchCalForm {...props} />;
  if (type === 'HYD_PRESSURE') return <HydPressureCalForm {...props} />;
  if (type === 'PNU') return <PneumaticCalForm {...props} />;
  if (type === 'GAUGE' || type === 'DIAL') return <GaugeCalForm {...props} />;
  if (type === 'HOSE_MDG41' || type === 'HOSE_JACK') return <HoseTestCalForm {...props} initialType={type} />;
  return null;
}

export default function CalibrationsTab({ job, canManage, assetType }) {
  const [cals, setCals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typePickerOpen, setTypePickerOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editCal, setEditCal] = useState(null);
  const [certState, setCertState] = useState({});
  const [allowedCalTypes, setAllowedCalTypes] = useState(null); // null = not loaded yet

  useEffect(() => {
    if (!assetType) { setAllowedCalTypes(null); return; }
    fetch(baseUrl + 'workshop/settings/cal-type-map', { headers: authHeaders() })
      .then(r => r.json())
      .then(rows => {
        if (!Array.isArray(rows)) { setAllowedCalTypes(null); return; }
        const mapped = rows.filter(r => r.assetType === assetType).map(r => r.calType);
        setAllowedCalTypes(mapped);
      })
      .catch(() => setAllowedCalTypes(null));
  }, [assetType]);

  const loadCals = () => {
    setLoading(true);
    fetch(baseUrl + 'workshop/jobs/' + job.id + '/calibrations', { headers: authHeaders() })
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : [];
        setCals(list);
        setLoading(false);
        list.forEach(cal => {
          fetch(baseUrl + 'workshop/jobs/' + job.id + '/calibrations/' + cal.id + '/certificate', { headers: authHeaders() })
            .then(r => r.ok ? r.json() : null)
            .then(cert => {
              if (cert) {
                setCertState(prev => ({ ...prev, [cal.id]: { uploadId: cert.uploadId, proxyUrl: cert.proxyUrl, generatedAt: cert.generatedAt } }));
              }
            })
            .catch(() => {});
        });
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { if (job?.id) loadCals(); }, [job?.id]);

  const handleGenerateCert = async (cal) => {
    setCertState(prev => ({ ...prev, [cal.id]: { ...prev[cal.id], generating: true } }));
    try {
      const res = await fetch(baseUrl + 'workshop/jobs/' + job.id + '/calibrations/' + cal.id + '/certificate', {
        method: 'POST', headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setCertState(prev => ({ ...prev, [cal.id]: { generating: false, uploadId: data.uploadId, proxyUrl: data.proxyUrl, generatedAt: new Date().toISOString() } }));
      } else {
        setCertState(prev => ({ ...prev, [cal.id]: { ...prev[cal.id], generating: false } }));
        alert('Certificate generation failed');
      }
    } catch {
      setCertState(prev => ({ ...prev, [cal.id]: { ...prev[cal.id], generating: false } }));
    }
  };

  const handleSelectType = (type) => {
    setSelectedType(type);
    setEditCal(null);
    setTypePickerOpen(false);
    setFormOpen(true);
  };

  const handleOpen = async (cal) => {
    const res = await fetch(baseUrl + 'workshop/jobs/' + job.id + '/calibrations/' + cal.id, { headers: authHeaders() });
    const full = await res.json();
    setEditCal(full);
    setSelectedType(full.type);
    setFormOpen(true);
  };

  const handleDelete = async (cal) => {
    if (!window.confirm(`Delete calibration cert ${cal.certNo || '#' + cal.id}? This cannot be undone.`)) return;
    await fetch(baseUrl + 'workshop/jobs/' + job.id + '/calibrations/' + cal.id, {
      method: 'DELETE', headers: authHeaders(),
    });
    loadCals();
  };

  const handleDownloadCert = async (e, proxyUrl, certNo) => {
    e.stopPropagation();
    try {
      const res = await fetch(baseUrl + proxyUrl, { headers: authHeaders() });
      if (!res.ok) { alert('Download failed'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (certNo || proxyUrl.split('/').pop()) + '.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch { alert('Download failed'); }
  };

  const handleFormClose = () => { setFormOpen(false); setEditCal(null); setSelectedType(null); };
  const handleSaved = () => { handleFormClose(); loadCals(); };

  return (
    <Box sx={{ pt: 1 }}>
      {canManage && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
          <Tooltip title={allowedCalTypes && allowedCalTypes.length === 0 ? 'No calibration types mapped for this asset type' : ''}>
            <span>
              <Button
                variant="outlined" size="small" startIcon={<AddIcon />}
                onClick={() => setTypePickerOpen(true)}
                disabled={allowedCalTypes !== null && allowedCalTypes.length === 0}
              >
                New Calibration
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}

      {loading && <CircularProgress size={24} />}

      {!loading && cals.length === 0 && (
        <Typography color="text.secondary">No calibration records.</Typography>
      )}

      {!loading && cals.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Cert No</TableCell>
              <TableCell>Cal Date</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Technician</TableCell>
              <TableCell>Result</TableCell>
              <TableCell>Certificate</TableCell>
              {canManage && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {cals.map(cal => {
              const cert = certState[cal.id] || {};
              return (
                <TableRow key={cal.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleOpen(cal)}>
                  <TableCell>
                    <Typography variant="body2">{CAL_TYPE_LABELS[cal.type] || cal.type}</Typography>
                  </TableCell>
                  <TableCell>{cal.certNo || '—'}</TableCell>
                  <TableCell>{cal.calibrationDate ? dayjs(cal.calibrationDate).format('DD/MM/YYYY') : '—'}</TableCell>
                  <TableCell>{cal.calDueDate ? dayjs(cal.calDueDate).format('DD/MM/YYYY') : '—'}</TableCell>
                  <TableCell>{cal.technician || '—'}</TableCell>
                  <TableCell>
                    {cal.overallResult
                      ? <Chip label={cal.overallResult} size="small" sx={{ bgcolor: cal.overallResult === 'PASS' ? 'var(--pass-bg)' : 'var(--fail-bg)', color: cal.overallResult === 'PASS' ? 'var(--pass)' : 'var(--fail)', fontWeight: 700 }} />
                      : <Typography variant="caption" color="text.secondary">Pending</Typography>
                    }
                  </TableCell>
                  <TableCell onClick={e => e.stopPropagation()}>
                    {(() => {
                      const certFresh = cert.proxyUrl && cert.generatedAt && cal.updatedAt
                        && new Date(cert.generatedAt) >= new Date(cal.updatedAt);
                      if (certFresh) {
                        return (
                          <Tooltip title="Download certificate PDF">
                            <IconButton size="small" onClick={(e) => handleDownloadCert(e, cert.proxyUrl, cal.certNo)}>
                              <DownloadIcon fontSize="small" color="primary" />
                            </IconButton>
                          </Tooltip>
                        );
                      }
                      if (!canManage) return null;
                      return (
                        <Tooltip title={cal.overallResult ? 'Generate certificate PDF' : 'Set overall result before generating'}>
                          <span>
                            <IconButton size="small" onClick={() => handleGenerateCert(cal)} disabled={cert.generating || !cal.overallResult}>
                              {cert.generating ? <CircularProgress size={16} /> : <PictureAsPdfIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      );
                    })()}
                  </TableCell>
                  {canManage && (
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      <IconButton size="small" onClick={() => handleOpen(cal)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => handleDelete(cal)}><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <TypePickerDialog open={typePickerOpen} onClose={() => setTypePickerOpen(false)} onSelect={handleSelectType} allowedCalTypes={allowedCalTypes} />

      <CalForm
        type={selectedType}
        open={formOpen}
        onClose={handleFormClose}
        job={job}
        cal={editCal}
        canManage={canManage}
        onSaved={handleSaved}
      />
    </Box>
  );
}
